/**
 * Simple Authentication Service
 * Clean OAuth2 + PKCE flow without complex state management
 */

class SimpleAuthService {
  constructor() {
    this.baseUrl = process.env.NODE_ENV === 'production' 
      ? 'https://app.mydew.life' 
      : '';
    
    // Clean up stale PKCE parameters on initialization
    this.cleanupStaleParameters();
  }

  /**
   * Clean up stale PKCE parameters (older than 10 minutes)
   */
  cleanupStaleParameters() {
    try {
      const stored = localStorage.getItem('pkce_timestamp');
      if (stored) {
        const timestamp = parseInt(stored);
        const now = Date.now();
        const tenMinutes = 10 * 60 * 1000; // 10 minutes in milliseconds
        
        if (now - timestamp > tenMinutes) {
          console.log('🧹 Cleaning up stale PKCE parameters...');
          localStorage.removeItem('pkce_code_verifier');
          localStorage.removeItem('pkce_state');
          localStorage.removeItem('pkce_timestamp');
        }
      }
    } catch (error) {
      console.warn('⚠️ Error cleaning up stale parameters:', error);
    }
  }

  /**
   * Generate PKCE parameters
   */
  async generatePKCE() {
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    return { codeVerifier, codeChallenge };
  }

  /**
   * Generate a random code verifier
   */
  generateCodeVerifier() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return this.base64URLEncode(array);
  }

  /**
   * Generate code challenge from verifier
   */
  async generateCodeChallenge(codeVerifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return this.base64URLEncode(new Uint8Array(digest));
  }

  /**
   * Base64 URL encode
   */
  base64URLEncode(buffer) {
    return btoa(String.fromCharCode(...buffer))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Generate random state parameter
   */
  generateState() {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return this.base64URLEncode(array);
  }

  /**
   * Initiate login - redirect to Cognito
   */
  async login() {
    try {
      // Generate PKCE parameters
      const { codeVerifier, codeChallenge } = await this.generatePKCE();
      const state = this.generateState();

      // Store in localStorage (persistent across redirects)
      localStorage.setItem('pkce_code_verifier', codeVerifier);
      localStorage.setItem('pkce_state', state);
      localStorage.setItem('pkce_timestamp', Date.now().toString());
      
      console.log('🔐 Stored PKCE parameters:', {
        codeVerifier: codeVerifier ? 'present' : 'missing',
        state: state ? 'present' : 'missing',
        timestamp: new Date().toISOString()
      });

      // Build login URL
      const loginUrl = new URL('https://auth.mydew.life/login');
      loginUrl.searchParams.set('client_id', '7comeci8c9v93llv08ehqvgviu');
      loginUrl.searchParams.set('response_type', 'code');
      loginUrl.searchParams.set('scope', 'email openid phone profile');
      // Use dynamic redirect URI based on current environment
      const redirectUri = window.location.origin + '/auth/callback';
      loginUrl.searchParams.set('redirect_uri', redirectUri);
      loginUrl.searchParams.set('code_challenge', codeChallenge);
      loginUrl.searchParams.set('code_challenge_method', 'S256');
      loginUrl.searchParams.set('state', state);

      // Redirect to Cognito
      window.location.href = loginUrl.toString();
    } catch (error) {
      console.error('❌ Login failed:', error);
      throw error;
    }
  }

  /**
   * Initiate signup - redirect to Cognito signup
   */
  async signup() {
    try {
      // Generate PKCE parameters
      const { codeVerifier, codeChallenge } = await this.generatePKCE();
      const state = this.generateState();

      // Store in localStorage (persistent across redirects)
      localStorage.setItem('pkce_code_verifier', codeVerifier);
      localStorage.setItem('pkce_state', state);
      localStorage.setItem('pkce_timestamp', Date.now().toString());
      
      console.log('🔐 Stored PKCE parameters for signup:', {
        codeVerifier: codeVerifier ? 'present' : 'missing',
        state: state ? 'present' : 'missing',
        timestamp: new Date().toISOString()
      });

      // Build signup URL (Cognito typically uses /signup endpoint)
      const signupUrl = new URL('https://auth.mydew.life/signup');
      signupUrl.searchParams.set('client_id', '7comeci8c9v93llv08ehqvgviu');
      signupUrl.searchParams.set('response_type', 'code');
      signupUrl.searchParams.set('scope', 'email openid phone profile');
      // Use dynamic redirect URI based on current environment
      const redirectUri = window.location.origin + '/auth/callback';
      signupUrl.searchParams.set('redirect_uri', redirectUri);
      signupUrl.searchParams.set('code_challenge', codeChallenge);
      signupUrl.searchParams.set('code_challenge_method', 'S256');
      signupUrl.searchParams.set('state', state);

      // Redirect to Cognito
      window.location.href = signupUrl.toString();
    } catch (error) {
      console.error('❌ Signup failed:', error);
      throw error;
    }
  }

  /**
   * Exchange authorization code for tokens
   */
  async exchangeCodeForTokens(code, state) {
    try {
      // Get stored parameters
      const codeVerifier = localStorage.getItem('pkce_code_verifier');
      const storedState = localStorage.getItem('pkce_state');

      console.log('🔍 Retrieving PKCE parameters:', {
        codeVerifier: codeVerifier ? 'present' : 'missing',
        storedState: storedState ? 'present' : 'missing',
        allLocalStorage: Object.keys(localStorage).filter(key => key.startsWith('pkce')),
        timestamp: new Date().toISOString()
      });

      // Check if code_verifier exists
      if (!codeVerifier) {
        console.error('❌ Missing code_verifier in localStorage. This usually means the login flow was interrupted.');
        console.error('❌ Available localStorage keys:', Object.keys(localStorage));
        
        // Clear any stale parameters and redirect to homepage
        localStorage.removeItem('pkce_code_verifier');
        localStorage.removeItem('pkce_state');
        localStorage.removeItem('pkce_timestamp');
        
        // Instead of throwing an error, redirect to homepage
        console.log('🔄 Redirecting to homepage to restart login flow...');
        window.location.href = '/';
        return;
      }

      // State validation - abort on mismatch to prevent CSRF / code injection
      if (state && storedState && state !== storedState) {
        console.error('❌ State mismatch - possible CSRF; aborting token exchange');
        localStorage.removeItem('pkce_code_verifier');
        localStorage.removeItem('pkce_state');
        localStorage.removeItem('pkce_timestamp');
        window.location.href = '/';
        return;
      }

      // Exchange code for tokens
      console.log('📡 Sending token exchange request to:', `${this.baseUrl}/api/auth/exchange-code`);
      
      // Create an AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      try {
        const response = await fetch(`${this.baseUrl}/api/auth/exchange-code`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          signal: controller.signal,
          body: JSON.stringify({
            code,
            code_verifier: codeVerifier,
            state
          })
        });

        clearTimeout(timeoutId);
        console.log('📥 Token exchange response status:', response.status, response.statusText);

        if (!response.ok) {
          let errorData;
          try {
            errorData = await response.json();
          } catch (e) {
            const errorText = await response.text();
            console.error('❌ Failed to parse error response:', errorText);
            throw new Error(`Token exchange failed: ${response.status} ${response.statusText}`);
          }
          console.error('❌ Token exchange error response:', errorData);
          throw new Error(errorData.error || 'Token exchange failed');
        }

        // Parse response
        const result = await response.json();
        console.log('✅ Token exchange successful, response:', {
          hasSuccess: !!result.success,
          hasUser: !!result.user,
          keys: Object.keys(result)
        });

        // Clear stored parameters
        localStorage.removeItem('pkce_code_verifier');
        localStorage.removeItem('pkce_state');
        localStorage.removeItem('pkce_timestamp');

        return result;
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          console.error('❌ Token exchange request timed out after 30 seconds');
          throw new Error('Request timeout - server did not respond');
        }
        throw fetchError;
      }
    } catch (error) {
      console.error('❌ Token exchange failed:', error);
      console.error('❌ Error type:', error.name);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error stack:', error.stack);
      
      // Clear stored parameters on error
      localStorage.removeItem('pkce_code_verifier');
      localStorage.removeItem('pkce_state');
      localStorage.removeItem('pkce_timestamp');
      throw error;
    }
  }

  /**
   * Check if user is authenticated with automatic token refresh
   */
  async checkAuth() {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/me`, {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const userData = await response.json();
        return { isAuthenticated: true, user: userData };
      } else if (response.status === 401) {
        // Token might be expired, try to refresh
        console.log('🔄 Token expired, attempting refresh...');
        const refreshSuccess = await this.attemptTokenRefresh();
        
        if (refreshSuccess) {
          // Retry the auth check after successful refresh
          const retryResponse = await fetch(`${this.baseUrl}/api/auth/me`, {
            method: 'GET',
            credentials: 'include'
          });
          
          if (retryResponse.ok) {
            const userData = await retryResponse.json();
            return { isAuthenticated: true, user: userData };
          }
        }
        
        // If refresh failed or retry failed, user needs to log in again
        return { isAuthenticated: false, user: null };
      } else {
        return { isAuthenticated: false, user: null };
      }
    } catch (error) {
      console.error('❌ Auth check failed:', error);
      return { isAuthenticated: false, user: null };
    }
  }

  /**
   * Attempt to refresh tokens using refresh token
   */
  async attemptTokenRefresh() {
    try {
      console.log('🔄 Attempting token refresh...');
      const response = await fetch(`${this.baseUrl}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        console.log('✅ Token refresh successful');
        return true;
      } else {
        console.log('❌ Token refresh failed:', response.status);
        return false;
      }
    } catch (error) {
      console.error('❌ Token refresh error:', error);
      return false;
    }
  }

  /**
   * Make authenticated request with automatic token refresh
   */
  async authenticatedRequest(url, options = {}) {
    try {
      const response = await fetch(url, {
        ...options,
        credentials: 'include', // Include cookies for session-based auth
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      // If unauthorized, try to refresh token first
      if (response.status === 401) {
        console.log('🔐 Unauthorized, attempting token refresh...');
        const refreshSuccess = await this.attemptTokenRefresh();
        
        if (refreshSuccess) {
          // Retry the request with refreshed tokens
          const retryResponse = await fetch(url, {
            ...options,
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              ...options.headers,
            },
          });
          
          if (retryResponse.ok) {
            return retryResponse;
          }
        }
        
        // If refresh failed or retry failed, redirect to login
        console.log('🔐 Token refresh failed, redirecting to login...');
        await this.login();
        return null;
      }

      return response;
    } catch (error) {
      console.error('❌ Authenticated request failed:', error);
      throw error;
    }
  }

  /**
   * Sign out
   */
  async signOut() {
    try {
      // Call server signout endpoint
      await fetch(`${this.baseUrl}/api/auth/signout`, {
        method: 'POST',
        credentials: 'include'
      });

      // Redirect to Cognito logout (use current origin so local works)
      const logoutUrl = new URL('https://auth.mydew.life/logout');
      logoutUrl.searchParams.set('client_id', '7comeci8c9v93llv08ehqvgviu');
      logoutUrl.searchParams.set('logout_uri', window.location.origin + '/');
      
      window.location.href = logoutUrl.toString();
    } catch (error) {
      console.error('❌ Sign out failed:', error);
      // Fallback: just redirect to home
      window.location.href = '/';
    }
  }
}

// Export singleton instance
export const sessionAuthService = new SimpleAuthService();
